import { useState } from 'react';
import { Share2, Link2, Check, Facebook, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackShareProduct } from '../utils/analytics';

/**
 * ShareButtons — copy-link + native share + 1-click platform shares.
 *
 * IMPORTANT: every platform-share URL points to /api/og/p/{productId}, NOT the
 * raw /product/{id} page. This is intentional. FB / Messenger / WhatsApp / X
 * scrapers don't run JS, so they can only read meta tags from raw HTML. Our
 * /api/og/p/* endpoint returns a server-rendered page with proper OG meta tags
 * AND a JS+meta-refresh that hands real users back to /product/{id}.
 */
export default function ShareButtons({ product }) {
  const [copied, setCopied] = useState(false);

  if (!product) return null;

  const origin = window.location.origin;
  // FB / Messenger / WhatsApp / LinkedIn block our /api/og/ endpoint at the Cloudflare
  // edge (platform-level security policy we cannot bypass). So share URLs point to the
  // direct product page. On those four platforms, share previews will show the global
  // "Default Share Preview" (set in admin → Configuration). On Twitter/X, Discord,
  // iMessage, Telegram the per-product OG tags from ProductSchema.js are picked up
  // correctly because those scrapers run JavaScript.
  const shareUrl = `${origin}/product/${product.id}`;
  // (cleanUrl kept identical to shareUrl now that we share the canonical page directly)
  const cleanUrl = shareUrl;
  const shareText = `Check out ${product.name} on JS Fragrance`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackShareProduct(product.id, 'copy-link');
      toast.success('Share link copied — paste anywhere for a rich preview');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text: shareText, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  // Wrap target URLs in encodeURIComponent for each platform
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
  const xHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const messengerHref = `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`;

  const Btn = ({ onClick, href, label, icon: Icon, testid, color = 'text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]' }) => {
    const cls = `inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 ${color} text-xs font-sans uppercase tracking-[0.12em] transition-colors`;
    if (href) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid} aria-label={label}>
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{label}</span>
        </a>
      );
    }
    return (
      <button onClick={onClick} className={cls} data-testid={testid} aria-label={label}>
        <Icon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  };

  return (
    <div className="mt-6 pt-5 border-t border-white/5" data-testid="share-buttons">
      <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-sans mb-3">Share</p>
      <div className="flex flex-wrap items-center gap-2">
        <Btn onClick={copyLink} icon={copied ? Check : Link2} label={copied ? 'Copied' : 'Copy Link'} testid="share-copy-link"
          color={copied ? 'text-[#1C7D3F] border-[#1C7D3F]' : 'text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]'} />
        <Btn href={fbHref} icon={Facebook} label="Facebook" testid="share-facebook" />
        <Btn href={waHref} icon={MessageCircle} label="WhatsApp" testid="share-whatsapp" />
        <Btn href={xHref} icon={Share2} label="X / Twitter" testid="share-x" />
        {typeof navigator !== 'undefined' && navigator.share && (
          <Btn onClick={nativeShare} icon={Share2} label="More" testid="share-native" />
        )}
      </div>
      <p className="text-[10px] text-white/30 font-sans mt-2.5">
        Twitter/X, Discord, iMessage & Telegram show this product's image and details automatically. Facebook, WhatsApp & Messenger show your default site preview (configurable in admin).
      </p>
    </div>
  );
}
